import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import useSessionStore from '../stores/sessionStore'
import useGameStore from '../stores/gameStore'
import questionsData from '../data/questions.json'
import { COMBO_LEVELS, VIBE_LEVELS, SCORING, TIMING } from '../data/constants'
import { ANSWER_HINTS } from '../data/answerHints'

const BASE_CHOICE_TARGET = 8
const ABSOLUTE_CHOICE_TARGET = 10

const shuffleArray = (array) => {
  const result = [...array]

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }

  return result
}

const flattenAnswers = (answers) => {
  if (!Array.isArray(answers)) return []
  return answers.flatMap((answer) => (Array.isArray(answer) ? answer : [answer]))
}

const ARTICLE_WORDS = new Set([
  'a',
  'an',
  'the',
  'this',
  'that',
  'these',
  'those',
  'some',
  'any',
  'another',
  'other',
  'each',
  'every',
  'my',
  'your',
  'his',
  'her',
  'our',
  'their',
])

const PREPOSITION_WORDS = new Set([
  'in',
  'on',
  'at',
  'to',
  'for',
  'with',
  'from',
  'of',
  'by',
  'after',
  'before',
  'about',
  'over',
  'under',
  'into',
  'onto',
  'through',
  'across',
  'around',
  'between',
  'against',
  'during',
  'without',
  'within',
  'beyond',
])

const AUXILIARY_WORDS = new Set([
  'am',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'do',
  'does',
  'did',
  'have',
  'has',
  'had',
  'will',
  'would',
  'can',
  'could',
  'should',
  'may',
  'might',
  'must',
])

const PARTICLE_WORDS = new Set([
  'up',
  'down',
  'off',
  'on',
  'out',
  'over',
  'away',
  'back',
  'along',
  'across',
  'through',
])

const sanitizeTokens = (tokens) =>
  tokens.filter((token) => typeof token === 'string' && token.trim().length > 0)

const ALL_ANSWER_TOKENS = Array.from(
  new Set(sanitizeTokens(questionsData.flatMap((question) => flattenAnswers(question.answers))))
)

const ALL_CHOICE_TOKENS = Array.from(
  new Set(sanitizeTokens(questionsData.flatMap((question) => question.choices || [])))
)

const GLOBAL_DISTRACTOR_POOL = Array.from(
  new Set([...ALL_ANSWER_TOKENS, ...ALL_CHOICE_TOKENS])
)

const categorizeToken = (token = '') => {
  const normalized = token.toLowerCase().trim()

  if (!normalized) return 'misc'
  if (normalized.includes(' ')) return 'phrase'
  if (ARTICLE_WORDS.has(normalized)) return 'article'
  if (PREPOSITION_WORDS.has(normalized)) return 'preposition'
  if (AUXILIARY_WORDS.has(normalized)) return 'auxiliary'
  if (PARTICLE_WORDS.has(normalized)) return 'particle'
  if (normalized.endsWith('ing')) return 'gerund'
  if (normalized.endsWith('ed')) return 'verb_past'
  if (normalized.endsWith('ly')) return 'adverb'
  if (normalized.length <= 2) return 'short'
  return 'content'
}

const CATEGORY_BUCKETS = (() => {
  const buckets = {}

  GLOBAL_DISTRACTOR_POOL.forEach((token) => {
    const category = categorizeToken(token)
    if (!buckets[category]) {
      buckets[category] = new Set()
    }
    buckets[category].add(token)
  })

  return Object.fromEntries(
    Object.entries(buckets).map(([category, tokens]) => [category, Array.from(tokens)])
  )
})()

const determineChoiceTarget = (answerCount) => {
  const desired = Math.max(BASE_CHOICE_TARGET, answerCount + 2)
  return Math.min(ABSOLUTE_CHOICE_TARGET, desired)
}

const generateDecoyChoices = (answers, baseChoices = [], neededCount) => {
  if (neededCount <= 0) return []

  const usedTokens = new Set(answers.map((answer) => answer.toLowerCase()))
  const decoys = []

  const tryAddCandidate = (candidate) => {
    if (!candidate) return false
    const normalized = candidate.toLowerCase()
    if (usedTokens.has(normalized)) return false
    decoys.push(candidate)
    usedTokens.add(normalized)
    return true
  }

  for (const choice of shuffleArray(baseChoices)) {
    if (decoys.length >= neededCount) break
    tryAddCandidate(choice)
  }

  if (decoys.length < neededCount) {
    const categories = Array.from(new Set(answers.map((answer) => categorizeToken(answer))))
    let attempts = 0

    while (decoys.length < neededCount && attempts < 3) {
      for (const category of categories) {
        if (decoys.length >= neededCount) break
        const pool = CATEGORY_BUCKETS[category]
        if (!pool?.length) continue

        for (const token of shuffleArray(pool)) {
          if (tryAddCandidate(token)) break
        }
      }
      attempts++
    }
  }

  if (decoys.length < neededCount) {
    for (const token of shuffleArray(GLOBAL_DISTRACTOR_POOL)) {
      if (decoys.length >= neededCount) break
      tryAddCandidate(token)
    }
  }

  return decoys.slice(0, neededCount)
}

const buildLimitedChoices = (choices = [], answers) => {
  const normalizedAnswers = flattenAnswers(answers)
  if (normalizedAnswers.length === 0) {
    return shuffleArray(choices)
  }

  const shuffledChoices = shuffleArray(choices)
  const requiredChoices = []
  const remainingChoices = [...shuffledChoices]

  const answerCounts = {}
  normalizedAnswers.forEach((answer) => {
    answerCounts[answer] = (answerCounts[answer] || 0) + 1
  })

  Object.entries(answerCounts).forEach(([answer, count]) => {
    for (let i = 0; i < count; i++) {
      const matchIndex = remainingChoices.findIndex((choice) => choice === answer)

      if (matchIndex !== -1) {
        requiredChoices.push(remainingChoices.splice(matchIndex, 1)[0])
      } else {
        requiredChoices.push(answer)
      }
    }
  })

  const targetSize = determineChoiceTarget(normalizedAnswers.length)
  const slotsLeft = Math.max(targetSize - requiredChoices.length, 0)

  if (slotsLeft === 0) {
    return shuffleArray(requiredChoices)
  }

  const decoys = generateDecoyChoices(normalizedAnswers, remainingChoices, slotsLeft)

  return shuffleArray([...requiredChoices, ...decoys])
}

const filterQuestionsByRange = (range) => {
  if (!range) return [...questionsData]
  const [start, end] = range
  return questionsData.filter((q) => q.id >= start && q.id <= end)
}

const choiceIsCorrect = (choice, answers) => {
  return answers.some((answer) =>
    Array.isArray(answer) ? answer.includes(choice) : answer === choice
  )
}

const answersMatch = (selectedAnswers, correctAnswers) => {
  if (!correctAnswers || selectedAnswers.length !== correctAnswers.length) {
    return false
  }

  return selectedAnswers.every((answer, index) => {
    const correct = correctAnswers[index]
    return Array.isArray(correct) ? correct.includes(answer) : correct === answer
  })
}

export default function GamePlayScreen({ mode, questionRange, freeModeIsRandom = false, onComplete, onExit }) {
  const [questions, setQuestions] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [timeLeft, setTimeLeft] = useState(TIMING.QUESTION_TIME_LIMIT)
  const [isPaused, setIsPaused] = useState(false)
  const [isResetFloating, setIsResetFloating] = useState(false)
  const [waitingForUserTap, setWaitingForUserTap] = useState(false)
  const [selectedIndices, setSelectedIndices] = useState([])
  const resetButtonRef = useRef(null)
  const isCompletedRef = useRef(false)

  const {
    currentQuestionIndex,
    combo,
    vibeGauge,
    sessionScore,
    currentAnswers,
    isAnswerCorrect,
    showFeedback,
    currentEffect,
    isFeverActive,
    startSession,
    nextQuestion,
    selectAnswer,
    removeLastAnswer,
    clearAnswers,
    submitAnswer,
    hideFeedback,
    endSession,
    getSessionSummary,
  } = useSessionStore()

  const {
    updateScore,
    updateQuestionProgress,
    updateHighScore,
    updateMaxCombo,
    incrementFeverCount,
  } = useGameStore()
  
  // getWeakQuestionsを安定した参照として取得
  const getWeakQuestions = useGameStore((state) => state.getWeakQuestions)

  // Initialize questions based on mode
  useEffect(() => {
    // 完了フラグをリセット
    isCompletedRef.current = false
    setIsInitialized(false)
    
    let questionSet = []

    if (mode === 'random') {
      // ランダムモード: 全問からランダムに20問ピックアップ
      const shuffled = shuffleArray([...questionsData])
      questionSet = shuffled.slice(0, 20)
    } else if (mode === 'weak') {
      // 苦手問題モード: 正答率の低い問題のみ
      const weakQuestions = getWeakQuestions(questionsData)
      if (weakQuestions.length > 0) {
        // ランダムにシャッフルして出題
        questionSet = shuffleArray(weakQuestions)
      } else {
        // 苦手問題がない場合は全問からランダム
        questionSet = shuffleArray([...questionsData]).slice(0, 20)
      }
    } else if (mode === 'free') {
      // フリーモード: 指定範囲から、順番通り or ランダム
      questionSet = questionRange ? filterQuestionsByRange(questionRange) : [...questionsData]
      
      // ランダムが選択されている場合はシャッフル
      if (freeModeIsRandom) {
        questionSet = shuffleArray(questionSet)
      }
      // 順番通りの場合はそのまま（ID順に並んでいる）
    } else {
      questionSet = [...questionsData]
    }

    if (questionSet.length === 0) {
      questionSet = [...questionsData]
    }

    const randomizedQuestions = questionSet.map((question) => ({
      ...question,
      choices: buildLimitedChoices(question.choices, question.answers),
    }))

    setQuestions(randomizedQuestions)
    startSession()
    setSelectedIndices([])
    setTimeLeft(TIMING.QUESTION_TIME_LIMIT)
    setIsInitialized(true)
  }, [mode, questionRange, freeModeIsRandom])

  // Timer
  useEffect(() => {
    if (isPaused || showFeedback || questions.length === 0 || isCompletedRef.current) return

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Time's up - treat as incorrect (タイムアウト処理は別で行う)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isPaused, showFeedback, questions, currentQuestionIndex])
  
  // タイムアウト時の処理を別のuseEffectで行う
  useEffect(() => {
    if (timeLeft === 0 && !showFeedback && !isCompletedRef.current && isInitialized) {
      handleSubmit(true)
      setTimeLeft(TIMING.QUESTION_TIME_LIMIT)
    }
  }, [timeLeft, showFeedback, isInitialized])

  // Check if FEVER mode just activated
  useEffect(() => {
    if (isFeverActive && combo === 30) {
      incrementFeverCount()
    }
  }, [isFeverActive, combo, incrementFeverCount])

  const currentQuestion = questions[currentQuestionIndex]

  useEffect(() => {
    const checkResetPosition = () => {
      if (!resetButtonRef.current) {
        setIsResetFloating(false)
        return
      }
      const rect = resetButtonRef.current.getBoundingClientRect()
      setIsResetFloating(rect.bottom > window.innerHeight - 24)
    }

    const raf = requestAnimationFrame(checkResetPosition)
    window.addEventListener('resize', checkResetPosition)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', checkResetPosition)
    }
  }, [currentQuestion, currentAnswers, showFeedback])

  // Helper function to reset all selections
  const handleClearAnswers = () => {
    clearAnswers()
    setSelectedIndices([])
  }

  const handleAnswerClick = (answer, index) => {
    if (showFeedback) return

    // Check if this specific button instance is already selected
    if (selectedIndices.includes(index)) return

    // Mark this button index as selected
    setSelectedIndices((prev) => [...prev, index])
    selectAnswer(answer)

    // Auto-submit if all blanks are filled
    if (currentAnswers.length + 1 === currentQuestion.answers.length) {
      // Wait a moment before submitting for better UX
      setTimeout(() => {
        handleSubmit(false, [...currentAnswers, answer])
      }, 300)
    }
  }

  const handleSubmit = (isTimeout, answers = currentAnswers) => {
    // 完了済みまたは質問がない場合は何もしない
    if (!currentQuestion || isCompletedRef.current) return

    const isCorrect = isTimeout ? false : answersMatch(answers, currentQuestion.answers)

    const timeBonus = isCorrect ? (timeLeft * SCORING.TIME_BONUS_PER_SECOND) : 0

    submitAnswer(isCorrect, SCORING.BASE_POINTS, timeBonus)
    updateQuestionProgress(currentQuestion.id, isCorrect)

    if (isCorrect) {
      updateMaxCombo(combo + 1)
    }

    // 正解の場合は自動で次へ、不正解の場合はユーザーのタップを待つ
    if (isCorrect) {
      setTimeout(() => {
        // タイムアウト中に完了していたら何もしない
        if (isCompletedRef.current) return
        
        hideFeedback()

        if (currentQuestionIndex + 1 >= questions.length) {
          handleComplete()
        } else {
          nextQuestion()
          setSelectedIndices([])
          setTimeLeft(TIMING.QUESTION_TIME_LIMIT)
        }
      }, TIMING.FEEDBACK_DISPLAY)
    } else {
      // 不正解の場合はユーザーのタップを待つ
      setWaitingForUserTap(true)
    }
  }

  const handleFeedbackTap = () => {
    if (!waitingForUserTap || isCompletedRef.current) return
    
    setWaitingForUserTap(false)
    hideFeedback()

    if (currentQuestionIndex + 1 >= questions.length) {
      handleComplete()
    } else {
      nextQuestion()
      setSelectedIndices([])
      setTimeLeft(TIMING.QUESTION_TIME_LIMIT)
    }
  }

  const handleComplete = () => {
    // 既に完了処理が行われていたら何もしない
    if (isCompletedRef.current) return
    isCompletedRef.current = true
    
    endSession()
    const summary = getSessionSummary()

    // Update persistent stats
    updateScore(summary.score)
    updateHighScore(mode, summary.score)

    onComplete(summary)
  }

  const getComboLabel = () => {
    const level = COMBO_LEVELS.find((l) => combo >= l.min && combo <= l.max)
    return level?.label
  }

  const getVibeLevel = () => {
    return VIBE_LEVELS.find((l) => vibeGauge >= l.min && vibeGauge <= l.max)
  }

  const vibeLevel = getVibeLevel()
  const comboLabel = getComboLabel()

  // 初期化中または完了後は読み込み画面を表示
  if (!isInitialized || !currentQuestion) {
    // 完了済みの場合は何も表示しない（親コンポーネントが画面切り替えを行う）
    if (isCompletedRef.current) {
      return null
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-200 via-purple-200 to-blue-200">
        <div className="text-purple-600 text-2xl font-bold">✨ 読み込み中... ✨</div>
      </div>
    )
  }

  // Calculate blanks for display
  const blanks = currentQuestion.answers.length

  const resetButtonClasses = `btn-neon text-white px-6 py-2 ${
    isResetFloating
      ? 'fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[min(320px,calc(100%-48px))] pointer-events-auto'
      : 'w-full md:w-auto mx-auto block'
  }`
  const floatingSpacerClass = isResetFloating ? 'pb-32' : ''

  return (
    <div
      className="w-full h-full relative overflow-hidden bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100"
    >

      <div className={`max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 h-full flex flex-col ${floatingSpacerClass}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2 sm:mb-3 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={onExit}
              className="text-purple-600 hover:text-pink-500 transition-colors text-lg sm:text-xl font-bold"
            >
              ✕
            </button>
            <span className="font-display text-purple-600 text-sm sm:text-base font-bold">
              #{currentQuestion.id} / {questionsData.length}
            </span>
          </div>

          <motion.div
            className={`font-display text-lg sm:text-xl md:text-2xl font-black px-3 sm:px-4 py-2 rounded-full ${
              timeLeft <= 10 
                ? 'bg-gradient-to-r from-red-400 to-pink-500 text-white animate-pulse shadow-lg' 
                : 'bg-gradient-to-r from-blue-400 to-purple-400 text-white shadow-lg'
            }`}
            animate={timeLeft <= 10 ? { scale: [1, 1.15, 1], rotate: [0, -5, 5, 0] } : {}}
            transition={{ duration: 0.5, repeat: Infinity }}
          >
            ⏱️ {timeLeft}
          </motion.div>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-2 sm:h-3 bg-gradient-to-r from-pink-200 to-purple-200 rounded-full mb-2 sm:mb-3 overflow-hidden flex-shrink-0 shadow-inner">
          <motion.div
            className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-full shadow-lg"
            initial={{ width: 0 }}
            animate={{
              width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
            }}
          />
        </div>

        {/* Japanese Question */}
        <motion.div
          key={`japanese-${currentQuestion.id}`}
          initial={{ opacity: 0, y: -20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="card-neon mb-2 sm:mb-3 text-center flex-shrink-0 py-3 sm:py-4"
        >
          <p className="text-base sm:text-lg md:text-xl text-purple-700 px-2 font-bold">
            {currentQuestion.japanese}
          </p>
        </motion.div>

        {/* English Template with Blanks */}
        <motion.div
          key={`english-${currentQuestion.id}`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-2 sm:mb-3 flex-1 min-h-0 flex items-center justify-center overflow-hidden"
        >
          <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-body text-purple-700 flex flex-wrap items-center justify-center gap-1 sm:gap-2 text-center px-2 font-semibold">
            {(() => {
              // 括弧部分を特殊なマーカー（__BLANK__）に置き換え
              let template = currentQuestion.english_template
              template = template.replace(/\([^)]*\)/g, '__BLANK__')
              
              // マーカーで分割
              const parts = template.split('__BLANK__')
              
              let blankIndex = 0
              return parts.map((part, index) => {
                const words = part.trim().split(/\s+/).filter(Boolean)
                const hasBlank = index < parts.length - 1
                
                return (
                  <span key={index} className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    {words.map((word, wordIndex) => (
                      <span key={wordIndex}>{word}</span>
                    ))}
                    {hasBlank && (
                      <motion.span
                        className={`inline-block min-w-[70px] sm:min-w-[90px] md:min-w-[100px] px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-xl border-3 border-dashed text-sm sm:text-base md:text-lg font-bold ${
                          currentAnswers[blankIndex]
                            ? 'border-pink-400 bg-gradient-to-br from-pink-200 to-purple-200 text-purple-700 shadow-lg'
                            : 'border-purple-300 bg-gradient-to-br from-pink-100 to-purple-100 text-purple-400'
                        }`}
                        animate={
                          currentAnswers[blankIndex]
                            ? { scale: [0.9, 1.1, 1] }
                            : {}
                        }
                        transition={{ duration: 0.3 }}
                      >
                        {currentAnswers[blankIndex++] || '___'}
                      </motion.span>
                    )}
                  </span>
                )
              })
            })()}
          </div>
        </motion.div>

        {/* Answer Choices */}
        <div className="mb-2 sm:mb-3 flex-shrink-0">
          {/* Reset Button */}
          {currentAnswers.length > 0 && !showFeedback && (
            <motion.button
              onClick={handleClearAnswers}
              ref={resetButtonRef}
              className={`${resetButtonClasses} mb-2 sm:mb-3 bg-gradient-to-r from-orange-400 to-pink-400`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
              whileTap={{ scale: 0.95 }}
            >
              🔄 選択をリセット
            </motion.button>
          )}
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-2 sm:mb-3">
            {currentQuestion.choices.map((choice, index) => {
              const isSelected = selectedIndices.includes(index)
              const isCorrectAnswer = choiceIsCorrect(choice, currentQuestion.answers)
              const showResult = showFeedback

              return (
                <motion.button
                  key={`${choice}-${index}`}
                  onClick={() => handleAnswerClick(choice, index)}
                  disabled={isSelected || showFeedback}
                  className={`btn-neon choice-button text-xs sm:text-sm md:text-base py-2 sm:py-2.5 font-bold ${
                    showResult && isCorrectAnswer
                      ? 'bg-gradient-to-r from-green-400 to-emerald-400 text-white border-green-500'
                      : showResult && isSelected && !isCorrectAnswer
                      ? 'bg-gradient-to-r from-red-400 to-pink-400 text-white border-red-500'
                      : isSelected
                      ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white border-pink-500'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                  } ${isSelected || showFeedback ? 'opacity-70 cursor-not-allowed' : ''}`}
                  whileHover={!isSelected && !showFeedback ? { scale: 1.05 } : {}}
                  whileTap={!isSelected && !showFeedback ? { scale: 0.95 } : {}}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {choice}
                </motion.button>
              )
            })}
          </div>
        </div>

        {/* Bottom Bar - Vibe & Score */}
        <div className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 pb-2 sm:pb-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <motion.span
              className="text-2xl sm:text-3xl md:text-4xl"
              animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {vibeLevel?.emoji}
            </motion.span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-purple-600 mb-0.5 sm:mb-1 font-bold">{vibeLevel?.label}</div>
              <div className="w-full sm:w-48 h-3 sm:h-4 bg-gradient-to-r from-pink-200 to-purple-200 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  className={`h-full rounded-full ${
                    isFeverActive
                      ? 'bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400'
                      : vibeGauge > 75
                      ? 'bg-gradient-to-r from-pink-500 to-purple-500'
                      : vibeGauge > 50
                      ? 'bg-gradient-to-r from-blue-400 to-purple-400'
                      : vibeGauge > 25
                      ? 'bg-gradient-to-r from-cyan-400 to-blue-400'
                      : 'bg-gradient-to-r from-pink-300 to-purple-300'
                  }`}
                  animate={{ width: `${vibeGauge}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>

          <div className="text-right sm:text-left md:text-right">
            <div className="text-purple-600 text-xs mb-0.5 sm:mb-1 font-bold">スコア</div>
            <motion.div
              className="font-display text-lg sm:text-xl md:text-2xl font-black bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent"
              key={sessionScore}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.3 }}
            >
              💎 {sessionScore.toLocaleString()}
            </motion.div>
          </div>
        </div>

      </div>

      {/* Feedback Overlay */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={waitingForUserTap ? handleFeedbackTap : undefined}
            className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-pink-200/80 via-purple-200/80 to-blue-200/80 backdrop-blur-md z-50 ${
              waitingForUserTap ? 'cursor-pointer' : ''
            }`}
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="text-center px-4"
            >
              {isAnswerCorrect ? (
                <>
                  <motion.div
                    className="text-8xl mb-4"
                    animate={{ scale: [1, 1.3, 1], rotate: [0, 360] }}
                    transition={{ duration: 0.6 }}
                  >
                    ✓
                  </motion.div>
                  <div className="text-5xl font-display font-black bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent mb-2">
                    {comboLabel || '正解！'} ✨
                  </div>
                  {combo > 0 && (
                    <motion.div
                      className="font-display text-purple-600 text-3xl mb-2 font-black"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                    >
                      {combo}x コンボ 🔥
                    </motion.div>
                  )}
                  {currentQuestion && (
                    <div className="text-xl text-purple-700 font-bold bg-white/80 px-4 py-2 rounded-xl">
                      {currentQuestion.full_answer}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <motion.div
                    className="text-8xl mb-4"
                    animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    ✗
                  </motion.div>
                  <div className="text-4xl font-display font-black bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent mb-2">
                    不正解！
                  </div>
                  {currentQuestion && (
                    <div className="text-lg text-purple-700 font-bold bg-white/80 px-4 py-2 rounded-xl mb-4">
                      正解: {currentQuestion.full_answer}
                    </div>
                  )}
                  {/* 解説セクション */}
                  {currentQuestion && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl p-4 mb-4 max-w-md mx-auto border-2 border-purple-300"
                    >
                      <div className="text-xs text-purple-600 font-bold mb-2">📖 ポイント解説</div>
                      <div className="space-y-2">
                        {(() => {
                          // 正解の単語から解説を取得
                          const normalizedAnswers = flattenAnswers(currentQuestion.answers).map(a => a.toLowerCase())
                          const uniqueAnswers = [...new Set(normalizedAnswers)]
                          const hints = uniqueAnswers
                            .map(answer => {
                              const hint = ANSWER_HINTS[answer]
                              return hint ? { key: answer, description: hint.description } : null
                            })
                            .filter(Boolean)
                            .slice(0, 3) // 最大3つまで表示

                          if (hints.length === 0) {
                            return (
                              <p className="text-purple-700 text-sm font-semibold">
                                この表現を覚えておきましょう！
                              </p>
                            )
                          }

                          return hints.map((hint, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <span className="bg-gradient-to-r from-pink-400 to-purple-400 text-white text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0">
                                {hint.key}
                              </span>
                              <p className="text-purple-700 text-sm font-semibold text-left">
                                {hint.description}
                              </p>
                            </div>
                          ))
                        })()}
                      </div>
                    </motion.div>
                  )}
                  {waitingForUserTap && (
                    <motion.button
                      onClick={handleFeedbackTap}
                      className="btn-neon px-8 py-3 bg-gradient-to-r from-blue-400 to-purple-400"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                      whileTap={{ scale: 0.95 }}
                    >
                      ✨ 次へ ✨
                    </motion.button>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
